import React, { useEffect, useRef, useState } from 'react'
import {
  Space,
  Button,
  Select,
  Divider,
  Row,
  Col,
  message,
  Typography
} from 'antd'
import { uuid } from 'uuidv4'
import { Product } from '../'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { GET_PRODUCT_ATTRIBUTES, REMOVE_ATTRIBUTE } from './graphql'
import { FIXED_ATTRIBUTES } from './attributes'
import { filter } from 'lodash'
import AttributeField from './field'
import { DeleteOutlined } from '@ant-design/icons'

type Attribute = {
  label: string
  name: string
  type: 'text' | 'string' | 'number'
}

interface AttributeFormProps {
  product: Product
}

const Attributes = (props: AttributeFormProps) => {
  const client = useApolloClient()
  const { product } = props
  const [attributes, setAttributes] = useState()
  const [emptyAttributes, setEmptyAttributes] = useState(
    Object.values(FIXED_ATTRIBUTES)
  )

  const selectRef = useRef(null)

  const [currentAttribute, setCurrentAttribute] = useState<
    Attribute | undefined
  >(undefined)

  const { data } = useQuery(GET_PRODUCT_ATTRIBUTES, {
    variables: {
      id: product.id
    },
    notifyOnNetworkStatusChange: true
  })
  const [removeProductAttribute, { loading: removingAttribute }] =
    useMutation(REMOVE_ATTRIBUTE)

  const removeAttributeHandler = (id: string, attribute: Attribute) => {
    removeProductAttribute({
      variables: {
        id,
        key: attribute.name
      }
    }).then((result) => {
      client.resetStore()
      message.destroy()
      message.success(`${attribute.label} was removed successfully`, 1)
    })
  }

  useEffect(() => {
    if (data && data?.product) {
      setAttributes(data?.product.attributes)
      setEmptyAttributes(
        filter(
          Object.values(FIXED_ATTRIBUTES),
          (attr) => !data?.product?.attributes[attr.name]
        )
      )
    }
    return () => {
      setAttributes(undefined)
      setCurrentAttribute(undefined)
      setEmptyAttributes(Object.values(FIXED_ATTRIBUTES))
    }
  }, [data, data?.product])

  return (
    <>
      <Row>
        <Col span={24}>
          <Space>
            <Typography.Text>Please select a new attribute</Typography.Text>
            <Select
              placeholder='Select attribute'
              allowClear
              ref={selectRef}
              onChange={(value) => setCurrentAttribute(FIXED_ATTRIBUTES[value])}
              defaultActiveFirstOption={true}
              style={{ width: 400 }}
              data-testid='attributes-dropdown'
            >
              {emptyAttributes.map((attr: Attribute) => {
                return (
                  <Select.Option
                    title={attr.label}
                    value={attr.name}
                    key={uuid()}
                  >
                    {attr.label}
                  </Select.Option>
                )
              })}
            </Select>
          </Space>
        </Col>
      </Row>
      {currentAttribute && (
        <>
          <AttributeField
            product={product}
            attribute={currentAttribute}
            onSave={() => {
              setCurrentAttribute(undefined)
              client.resetStore()
            }}
          />
          <Divider />
        </>
      )}
      {attributes &&
        Object.keys(attributes).map((key) => {
          const attribute = FIXED_ATTRIBUTES[key]
          const value = data?.product.attributes[key]
          return (
            <>
              <Row>
                <Col span={20}>
                  <AttributeField
                    product={product}
                    attribute={attribute}
                    defaultValue={value}
                    onSave={() => {
                      setCurrentAttribute(undefined)
                      client.resetStore()
                    }}
                  />
                </Col>
                <Col>
                  <Button
                    loading={removingAttribute}
                    size='small'
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      removeAttributeHandler(product.id, attribute)
                    }
                  />
                </Col>
              </Row>
              <Divider />
            </>
          )
        })}
    </>
  )
}

export default Attributes
